import { useQuery } from "@tanstack/react-query";
import Immutable from "immutable";
import React from "react";
import AppActions from "../../actions/AppActions";
import DomainActions from "../../actions/DomainActions";
import SiteActions from "../../actions/SiteActions";
import UIActions from "../../actions/UIActions";
import UserActions from "../../actions/UserActions";
import Button from "../../components/base/Button";
import session from "../../config/session";
import AppConstants from "../../constants/AppConstants";
import { MIGRATION_TYPE_PRIMARY } from "../../constants/MigrationConstants";
import { SITE } from "../../constants/QueryKeys";
import { COMMANDS } from "../../document/commands";
import DocumentStore from "../../document/DocumentStore";
import { readSiRouterFlag } from "../../router/flag";
import APIError from "../../service/APIError";
import DesignVersionUtils from "../../utils/DesignVersionUtils";
import { updateProcessingState } from "../../utils/ProcessingStateUtils";
import { queryClient, IMPERATIVE_CACHE_DEFAULTS } from "../../utils/QueryUtils";
import { persistParams } from "../../utils/UrlUtils";
import { getUserId } from "./useAccount";

/**
 * @typedef {Object} BlogDomain
 * @prop {string} domain_name
 * @prop {number|null} wp_host_id
 */

/**
 * @typedef {Object} DomainInfo
 * @prop {string} domain_name
 * @prop {boolean} www_required
 * @prop {number} ssl_enabled
 */

/**
 * @typedef {Object} SiteState
 * @prop {string=} blog_domain
 * @prop {string=} domain
 * @prop {DomainInfo=} domain_info
 * @prop {string=} key
 * @prop {string=} name
 * @prop {(number|null)=} site_design_id
 * @prop {number=} site_design_user - owner's user id
 * @prop {(number|null)=} site_domain_id
 * @prop {string=} subdomain
 * @prop {(number|null)=} wp_host_id
 */

/** @typedef {import("@/types/TypeUtils.mjs").ImmutableMapFrom<SiteState>} ImmutableSiteState */
/**
 * @template T
 * @typedef {import("@/types/TypeUtils.mjs").QueryResultRest<T>} QueryResultRest
 */
/**
 * @template T
 * @typedef {import("@/types/TypeUtils.mjs").QueryHookOptions<T>} QueryHookOptions
 */

/**
 * Empty-cache shape, mirroring the old `SiteStore` `model` it seeded `Immutable.fromJS(model)` with.
 * @type {SiteState}
 */
const model = {
  site_design_id: null,
};

/**
 * Boot/migration-retry flags rehomed from `SiteStore`'s instance fields (`initialLoad`/`initialPageId`/`hadSuccessfulMigration`).
 */
const siteLoaderState = {
  initialLoad: true,
  initialPageId: null,
  hadSuccessfulMigration: false,
};

/** Resets the module-local loader flags to their initial values. */
export const resetSiteLoaderState = () => {
  siteLoaderState.initialLoad = true;
  siteLoaderState.initialPageId = null;
  siteLoaderState.hadSuccessfulMigration = false;
};

const defaults = IMPERATIVE_CACHE_DEFAULTS;

// Register defaults on the shared client so non-hook callers inherit the same settings as `useSite`.
queryClient.setQueryDefaults([SITE], defaults);

/**
 * One-shot, non-triggering read of the cached site payload (plain JS) for non-React callers.
 * @returns {SiteState | undefined}
 */
export const getSiteData = () =>
  /** @type {SiteState | undefined} */ (queryClient.getQueryData([SITE]));

/**
 * Writes the SITE cache entry for non-React callers.
 * @param {(prev: SiteState | undefined) => SiteState} updater
 */
export const setSiteData = (updater) => queryClient.setQueryData([SITE], updater);

// One conversion per cache write, shared by every consumer (see useAccount.js).
/** @type {SiteState | null | undefined} */
let lastSiteData;
/** @type {ImmutableSiteState | undefined} */
let lastSiteState;
/**
 * @param {SiteState | null | undefined} data
 * @returns {ImmutableSiteState}
 */
const toSiteState = (data) => {
  if (data !== lastSiteData || !lastSiteState) {
    lastSiteData = data;
    lastSiteState = Immutable.fromJS(data ?? model);
  }
  return lastSiteState;
};

/**
 * Immutable-shaped read of the current site, for `getState()` consumers that fan `.get()`/`.getIn()` through shared utilities.
 * @returns {ImmutableSiteState}
 */
export const getSiteState = () => toSiteState(getSiteData());

/**
 * @returns {(string|number|null)} the current site's design id, or `null` when no site is loaded. Preserves the numeric type the server/`SITE_KEY` priming store -- load-bearing for `deepLinkSite.js`'s strict `!==` compare.
 */
export const getSiteId = () => getSiteData()?.site_design_id ?? null;

/** @returns {string=} the current site's user id */
export const getSiteUserID = () => getSiteData()?.site_design_user;

/** @returns {string=} the current site's design key */
export const getSiteKey = () => getSiteData()?.key;

/** @returns {string=} the current site's name */
export const getSiteName = () => getSiteData()?.name;

/**
 * Merges `site` into the cached site (shallow, top-level), mirroring the old `SiteStore.updateSite`.
 * @param {SiteState} site
 */
export const updateSite = (site) => {
  setSiteData((prev) => ({ ...(prev ?? model), ...site }));
};

/** `UserActions.initialize` seam. */
export const handleInitialize = () => {
  const raw = localStorage.getItem(AppConstants.SITE_KEY);
  if (raw) {
    const siteInfo = JSON.parse(raw);
    setSiteData((prev) => ({
      ...(prev ?? model),
      site_design_id: siteInfo.id,
      site_design_user: siteInfo.uid,
    }));
    siteLoaderState.initialPageId = siteInfo.init_page_id ?? null;
  }
};

/** `UserActions.logout` seam. */
export const handleLogout = () => {
  setSiteData(() => ({ ...model }));
  localStorage.removeItem(AppConstants.SITE_KEY);
  session.siteId = session.siteKey = null;
};

/**
 * `SiteActions.loadSite.complete` seam.
 * @param {{eTag: string, site: SiteState, spec: Object}} data
 */
export const handleLoadSiteComplete = (data) => {
  const {
    eTag,
    site,
    spec, // JSON the specification of the site design stored in a file
  } = data;

  const isCorrectDesignVersion = DesignVersionUtils.isCorrectVersion(spec?.version);
  const { key, site_design_id } = site;

  if (!isCorrectDesignVersion) {
    updateProcessingState({
      processing: true,
      message: "Preparing design...",
      overlay: true,
    });
    const migrationConfig = {
      key,
      migrationType: MIGRATION_TYPE_PRIMARY,
    };

    // design was migrated, but is still on the wrong version, force migration
    if (siteLoaderState.hadSuccessfulMigration) {
      migrationConfig.force = true;
      siteLoaderState.hadSuccessfulMigration = false;
    }

    DesignVersionUtils.migrateDesign(migrationConfig)
      .then(({ isSuccess = false }) => {
        if (isSuccess) {
          siteLoaderState.hadSuccessfulMigration = true;
        }
        SiteActions.loadSite({ site_design_id });
      })
      .catch(handleLoadSiteFail);

    return;
  }
  if (siteLoaderState.hadSuccessfulMigration) {
    siteLoaderState.hadSuccessfulMigration = false;
  }

  localStorage.setItem(
    AppConstants.SITE_KEY,
    JSON.stringify({ id: site_design_id, uid: getUserId() }),
  );

  session.siteId = site_design_id;
  session.siteKey = site.key;

  // Written first so `getSiteId()`/`getSiteState()` are accurate before `setDesign`/`showDesignView` read them.
  setSiteData((prev) => ({ ...(prev ?? model), ...site, site_design_user: getUserId() }));

  const designData = {
    design: spec,
    eTag,
  };

  if (siteLoaderState.initialLoad && siteLoaderState.initialPageId) {
    designData.pageId = siteLoaderState.initialPageId;
  }

  DocumentStore.dispatch({ type: COMMANDS.SET_DESIGN, payload: designData });
  UIActions.showDesignView();
  siteLoaderState.initialLoad = false;

  updateProcessingState({ processing: false, message: "", overlay: false, delay: 400 });

  // Intercom `?dk:` query param; superseded flag-on (the router URL already encodes the site).
  if (!readSiRouterFlag() && history.pushState) {
    const { protocol, host, pathname } = window.location;
    const validParams = persistParams(new URLSearchParams(window.location.search));
    const persistedParams = validParams.toString();
    const paramString = `dk:${site.key}${persistedParams ? `&${persistedParams}` : ""}`;

    const newUrl = `${protocol}//${host}${pathname}?${paramString}`;
    window.history.pushState({ path: newUrl }, "", newUrl);
  }
};

/**
 * `SiteActions.loadSite.fail` seam.
 * @param {Error} err
 */
export function handleLoadSiteFail(err) {
  siteLoaderState.initialLoad = false;
  console.log("Failed To Load Site", err);
  updateProcessingState({
    overlay: true,
    processing: false,
    error: (onClose) =>
      React.createElement(
        React.Fragment,
        null,
        React.createElement(
          "p",
          null,
          "There was an error loading your design, please contact support.",
        ),
        React.createElement(
          Button,
          {
            look: "primary",
            onClick: () =>
              onClose().then(() => {
                updateProcessingState({ reset: true });
              }),
          },
          "Dismiss",
        ),
      ),
  });

  localStorage.removeItem(AppConstants.SITE_KEY);
  if (APIError.handle(err)) {
    return;
  }
}

/**
 * `AppActions.storeValue` seam.
 * @param {string} key
 * @param {*} value
 */
export const handleStoreValue = (key, value) => {
  if (AppConstants.SITE_INIT_PAGE_KEY === key && value) {
    localStorage.setItem(
      AppConstants.SITE_KEY,
      JSON.stringify({
        id: getSiteId(),
        init_page_id: value,
        uid: getUserId(),
      }),
    );
  }
};

/**
 * `DomainActions.domainSettingsUpdated` seam.
 * @param {Object} domain
 */
export const handleDomainSettingsUpdated = (domain) => {
  const siteDomainId = getSiteData()?.site_domain_id;
  // biome-ignore lint/suspicious/noDoubleEquals: preserved from SiteStore.onDomainSettingsUpdated -- site_domain_id and site_id can differ in type (string vs number), so the loose compare is intentional for parity.
  if (siteDomainId == domain?.site_id) {
    setSiteData((prev) => {
      const base = prev ?? model;
      return {
        ...base,
        domain_info: { ...(base.domain_info ?? {}), www_required: domain?.www_required },
      };
    });
  }
};

/**
 * `SiteActions.domainConnect` seam.
 * @param {DomainInfo} domain_info
 */
export const handleDomainConnect = (domain_info) => {
  domain_info.ssl_enabled = domain_info.ssl_enabled || 0;
  setSiteData((prev) => ({
    ...(prev ?? model),
    domain_info,
    domain: domain_info.domain_name,
  }));
};

/**
 * `SiteActions.updateRendererVersion` seam.
 * @param {number} version - RendererType value (1 = legacy, 2 = v2 engine)
 */
export const handleUpdateRendererVersion = (version) => {
  setSiteData((prev) => ({
    ...(prev ?? model),
    renderer: version,
  }));
};

/**
 * `SiteActions.domainConnectBlog` seam.
 * @param {BlogDomain} blogDomain
 */
export const handleDomainConnectBlog = (blogDomain) => {
  setSiteData((prev) => ({
    ...(prev ?? model),
    wp_host_id: blogDomain.wp_host_id,
    blog_domain: blogDomain.domain_name,
  }));
};

// ---- Module-level action -> cache-write bridges.
UserActions.initialize.listen(handleInitialize);
UserActions.logout.listen(handleLogout);
AppActions.storeValue.listen(handleStoreValue);
DomainActions.domainSettingsUpdated.listen(handleDomainSettingsUpdated);
SiteActions.loadSite.complete.listen(handleLoadSiteComplete);
SiteActions.loadSite.fail.listen(handleLoadSiteFail);
SiteActions.domainConnect.listen(handleDomainConnect);
SiteActions.updateRendererVersion.listen(handleUpdateRendererVersion);
SiteActions.domainConnectBlog.listen(handleDomainConnectBlog);

/**
 * Subscribes a React component to the current site (TanStack replacement for `useStoreState(SiteStore, SiteStore.getState)` / scalar selectors).
 * @param {QueryHookOptions<SiteState | null>} [options] - Extra `useQuery` options, spread over the defaults.
 * @returns {{ site: SiteState | null | undefined } & QueryResultRest<SiteState | null>} `null` only if the no-op queryFn ever runs.
 */
const useSite = (options = {}) => {
  const { data, ...rest } = useQuery({
    queryKey: [SITE],
    // SITE is fed imperatively (enabled:false); a no-op queryFn silences TanStack's Missing-queryFn log.
    queryFn: () => getSiteData() ?? null,
    ...defaults,
    ...options,
  });
  return { site: data, ...rest };
};

/**
 * Immutable-shaped hook for the `getState()` component consumers (`SiteInfo`, `SiteInformation`, `SitePanel`).
 * @returns {ImmutableSiteState}
 */
export const useSiteState = () => {
  const { site } = useSite();
  return toSiteState(site);
};

export default useSite;
